import Resource from './Resource';
import AppService from 'App/legacy/AppService';
import parseCustom from 'App/resources/parsers/custom';
import { globalTracker } from 'Helpers/Tracker';
import { TYPE } from 'Store/reducers/SystemNotificationReducer';
import type CustomFieldModel from 'App/resources/Model/CustomField';

/*
	Standalone CustomField (WEB-7780, extended in WEB-11218). Ports the read members CacheRefresher
	needs plus the write members the React callers need off the Angular factory in
	upsales/common/resources/customField.js. What is left on that factory (getCached/findIdMap/get/
	setId) has no React caller; cacheRefresher.js still injects the factory by Angular DI.

	Faithfulness notes:

	- Angular declared `transformResponse: ParseGeneric('ParseCustom', { isArray: true,
	  skipDateConvert: true })`, which is exactly `res.data = res.data.map(parseCustom)` — no date
	  conversion and no custom-field expansion (`opts.custom` is falsy). `Resource` reproduces that
	  only when no attributes are passed to `super()`: `_parse` then runs `parseDates(field, [])`
	  (a no-op with the default empty `dateFields`), skips `genericParser`, and calls `parse`.
	  Passing an attributes object here would silently start running `genericParser` over every
	  field and change the payload.
	- `ParseCustom` is a one-line Angular wrapper around `App/resources/parsers/custom`, so the
	  parser below is literally the same function the $resource used.
	- `find({})` collapses to `params: undefined` in `Resource.find`, so no query string is sent —
	  matching Angular, whose params were consumed entirely by the URL template.
	- The `userDefinedN` → `userDefinedObject/{N}` rewrite that `typePath` does is asymmetric in the
	  Angular factory: save/saveMulti/delete run it, `find` does not (it GETs `/customfields/
	  userDefined1/`). That asymmetry is reproduced here — the find members pass the raw type to
	  `url()`, the write members pass it through `typePath` first.
	- Angular broadcast `customField.added`/`updated`/`deleted` through ResourceHelper. Nothing in
	  `ui/` listens for any of the three, so `eventName` is deliberately unset rather than newly
	  putting three unconsumed events on the EventBus. That also disables the analytics half of
	  `Resource._triggerEvent`, so the two tracks ResourceHelper fired (`Created`/`Updated
	  customField`, on save and saveMulti but never on delete) are kept as explicit calls.
	- Notification copy is ResourceHelper's super-defaults plus the one override
	  `deleteSucccessBody: 'deleted.field'` ("Field deleted"), which differs from `Resource`'s
	  `default.wasDeleted` and so has to be spelled out rather than left to `getDefaultNotifications`.
	- `_url` has no trailing slash: `_getUrl` appends the (empty) path with a `/`, producing
	  `.../customfields/{type}/`. The API routes are `/customfields/{type}/?$` and
	  `/customfields/userDefinedObject/:nr/?$`, so the trailing slash is accepted. (Angular's
	  $resource stripped it; one optional slash is the only difference in the request.)
*/
const typePath = (type: string) => type.replace(/^userDefined([1-4])$/, 'userDefinedObject/$1');

class CustomFieldResource extends Resource {
	// The base is untyped .js, so the field it reads off `this` needs declaring here. `opts` is
	// optional because Resource.save calls notifications.save() with no arguments on its dontWait
	// path.
	notifications: { [method: string]: (data?: unknown, opts?: { isNew?: boolean }) => object };

	constructor() {
		super('customfields');

		this.notifications = {
			save: (_data, opts) => ({
				title: opts?.isNew ? 'default.saved' : 'default.updated',
				body: opts?.isNew ? 'default.created' : 'default.updated',
				type: TYPE.BODY
			}),
			saveError: () => ({ title: 'default.error', body: 'default.error', type: TYPE.BODY }),
			delete: () => ({ title: 'default.deleted', body: 'deleted.field', type: TYPE.BODY }),
			deleteError: () => ({ title: 'default.error', body: 'default.error', type: TYPE.BODY })
		};
	}

	private url(segment: string) {
		return `${AppService.getCustomerId()}/customfields/${segment}`;
	}

	// Opt the whole entity out of `Resource._map`. Angular's $resource sent the object verbatim,
	// while `_map` ends in `mapDates`, which rewrites any ISO-8601 string longer than 15 characters
	// — a Date custom field's `default` — into a timezone-shifted one.
	_map(data: CustomFieldModel): CustomFieldModel {
		return data;
	}

	parse(field: CustomFieldModel): CustomFieldModel {
		return parseCustom(field);
	}

	findByType(type: string): Promise<CustomFieldModel[]> {
		return super.find({}, { _url: this.url(type) }).then((res: { data: CustomFieldModel[] }) => res.data);
	}

	findUdo(nr: number): Promise<CustomFieldModel[]> {
		return super
			.find({}, { _url: this.url(`userDefinedObject/${nr}`) })
			.then((res: { data: CustomFieldModel[] }) => res.data);
	}

	saveByType(type: string, field: CustomFieldModel): Promise<{ data: CustomFieldModel }> {
		const isNew = !field.id;

		return super.save(field, { _url: this.url(typePath(type)) }).then((res: { data: CustomFieldModel }) => {
			globalTracker.track(isNew ? 'Created customField' : 'Updated customField');
			return res;
		});
	}

	saveMultiByType(type: string, fields: CustomFieldModel[]): Promise<CustomFieldModel[]> {
		// Angular's `updateMulti` declared `isArray: true` and no transformResponse, so the array
		// comes back unparsed — hence `_putRequest` rather than `save`. It really is a bare array
		// and not the usual `{ error, data }` envelope: this hits the PathType.SPECIAL route, and
		// generic.js only wraps a special response when the endpoint sets `mapSpecial`, which the
		// customfield options do not. `isNew: false` picks the "updated" notification copy, which
		// is what ResourceHelper.onSave did when called without an isNew.
		return this._putRequest('', fields, {
			_url: this.url(typePath(type)),
			methodName: 'save',
			isNew: false
		}).then((res: { data: CustomFieldModel[] }) => {
			globalTracker.track('Updated customField');
			return res.data;
		});
	}

	deleteByType(type: string, field: CustomFieldModel) {
		return super.delete(field.id, { _url: this.url(typePath(type)) });
	}
}

export default new CustomFieldResource();
